import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Bone, Zap, Crown, Anchor, Sparkles, Stethoscope,
  MessageCircle, Printer, Mail, ChevronDown, ChevronUp, FileText, Check,
  AlertTriangle, Bug, CircleDot, Clock, Droplet, ShieldAlert,
} from "lucide-react";

// Map backend `icon` strings -> lucide icons
const ICONS = { Bone, Zap, Crown, Anchor, Sparkles, Stethoscope };
const RISK_ICONS = { Bug, CircleDot, Clock, Droplet, ShieldAlert };

// Small helper to open a printable window with a nicely styled HTML sheet.
// SECURITY NOTE:
//   - `document.write` here is SAFE by construction:
//     • It writes to a brand-new popup (`window.open("", "_blank", ...)`)
//       — never to the main document / origin.
//     • `html` is passed through `DOMPurify.sanitize(...)` (strips <script>,
//       on* handlers, iframes, etc.).
//     • `title` is HTML-entity-escaped below before interpolation.
//   Static analyzers flag `document.write` mechanically; this call is not
//   an XSS surface. Do NOT "refactor" to innerHTML on the parent document.
import DOMPurify from "isomorphic-dompurify";
import { DateDMYInput } from "@/components/DateDMYInput";
function openPrint(html, title) {
  const w = window.open("", "_blank", "width=700,height=900");
  if (!w) return toast.error("Popup blocked — please allow popups to print.");
  const safeTitle = String(title || "").replace(/[<>&"']/g, (c) =>
    ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;" }[c])
  );
  const safeHtml = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  w.document.write(`<!DOCTYPE html><html><head><title>${safeTitle}</title>
    <meta charset="utf-8"/>
    <style>
      body{font-family:Georgia,serif;color:#1A2D25;max-width:640px;margin:32px auto;padding:0 24px;line-height:1.6}
      h1{border-bottom:2px solid #1D3B2F;padding-bottom:8px}
      ul{padding-left:18px}
      li{margin:4px 0}
    </style></head><body>${safeHtml}</body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 300);
}

export default function PostOpInstructions({ patient }) {
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);   // full generated instruction doc
  const [expanded, setExpanded] = useState(false);
  const [followUp, setFollowUp] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    if (!patient?.id) return;
    const [t, h] = await Promise.all([
      api.get("/instruction-templates"),
      api.get(`/patients/${patient.id}/instructions`),
    ]);
    setTemplates(t.data);
    setHistory(h.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patient?.id]);

  const generate = async (templateKey) => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/instructions`, {
        template_key: templateKey,
        follow_up_date: followUp || null,
        extra_notes: extraNotes || null,
      });
      setSelected(data);
      setExpanded(true);
      toast.success("Instruction sheet generated");
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
    finally { setGenerating(false); }
  };

  const sendWhatsApp = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const { data } = await api.post(`/patient-instructions/${selected.id}/whatsapp`);
      if (data.status === "mocked" || data.provider === "mock") {
        toast.info("WhatsApp queued (mocked — configure WhatsApp Business API keys in .env to send for real)");
      } else if (data.status === "failed") {
        toast.error(`WhatsApp failed: ${data.error || "unknown error"}`);
      } else {
        toast.success(`Sent via ${data.provider || "WhatsApp"}`);
      }
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
    finally { setSending(false); }
  };

  const printSheet = async () => {
    if (!selected) return;
    // Fetch full doc with html
    const { data } = await api.get(`/patient-instructions/${selected.id}`);
    openPrint(data.content_html, data.title);
  };

  const emailSheet = async () => {
    if (!selected) return;
    if (!patient?.email) return toast.error("Patient has no email on file. Update the patient profile.");
    setSending(true);
    try {
      const { data } = await api.post(`/patient-instructions/${selected.id}/email`);
      if (data.status === "mocked") {
        toast.info("Email mocked — configure RESEND_API_KEY to send for real");
      } else {
        toast.success(`Email sent to ${patient.email}`);
      }
      load();
    } catch (err) {
      toast.error(apiError(err, "Email failed"));
    } finally {
      setSending(false);
    }
  };

  const previewLines = useMemo(() => {
    if (!selected) return [];
    return selected.content_text.split("\n");
  }, [selected]);

  return (
    <div className="card-widget" data-testid="postop-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-ink-mid" />
          <div className="overline text-ink-soft">Post-op instructions</div>
        </div>
        {history.length > 0 && (
          <button
            data-testid="postop-history-toggle"
            onClick={() => setHistoryOpen(!historyOpen)}
            className="text-xs text-ink-mid hover:text-ink inline-flex items-center gap-1"
          >
            {history.length} sent {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      <p className="text-xs text-ink-mid mb-4">Pick a procedure — we&apos;ll auto-fill a patient-friendly instruction sheet for {patient?.name || "this patient"}. Then send it via WhatsApp, print, or email.</p>

      {/* Template picker grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="postop-templates">
        {templates.map((tpl) => {
          const Icon = ICONS[tpl.icon] || Stethoscope;
          const isSelected = selected?.template_key === tpl.key;
          return (
            <button
              key={tpl.key}
              data-testid={`postop-tpl-${tpl.key}`}
              disabled={generating}
              onClick={() => generate(tpl.key)}
              className={`group relative flex items-center gap-3 px-3 py-3 rounded-2xl border transition-all text-left ${
                isSelected
                  ? "bg-pine text-linen border-pine"
                  : "border-[#E8EAE9] hover:border-pine/40 hover:bg-linen/40 text-ink"
              } disabled:opacity-60 disabled:cursor-wait`}
            >
              <div className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${
                isSelected ? "bg-linen/20" : "bg-linen"
              }`}>
                <Icon className={`w-4 h-4 ${isSelected ? "text-linen" : "text-ink"}`} />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate capitalize">{tpl.key}</div>
                <div className={`text-[10px] truncate ${isSelected ? "text-linen/70" : "text-ink-soft"}`}>{tpl.duration_hint}</div>
              </div>
              {isSelected && <Check className="w-3.5 h-3.5 absolute top-2 right-2 text-linen" />}
            </button>
          );
        })}
      </div>

      {/* Optional add-ons (follow-up + notes) */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Follow-up date (optional)</label>
          <DateDMYInput
            data-testid="postop-followup"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Extra notes from you (optional)</label>
          <input
            type="text"
            data-testid="postop-notes"
            placeholder='e.g., "Avoid gym for 3 days"'
            value={extraNotes}
            onChange={(e) => setExtraNotes(e.target.value)}
            className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* Generated preview + actions */}
      {selected && (
        <div className="mt-5 rounded-2xl bg-linen/40 border border-[#E8EAE9]" data-testid="postop-preview">
          <div className="p-4 border-b border-[#E8EAE9] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display text-lg text-ink">{selected.title}</div>
              <div className="text-[11px] text-ink-soft">Generated for {patient?.name}</div>
            </div>
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-ink-mid hover:text-ink inline-flex items-center gap-1">
              {expanded ? "Collapse" : "Expand"} {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
          {expanded && (
            <div className="p-4 max-h-72 overflow-y-auto text-sm text-ink/80 font-mono leading-relaxed whitespace-pre-wrap" data-testid="postop-preview-text">
              {previewLines.map((line, i) => {
                if (line.startsWith("*") && line.endsWith("*")) {
                  return <div key={i} className="font-display text-ink mt-3 mb-1 not-italic">{line.replaceAll("*", "")}</div>;
                }
                return <div key={i}>{line || "\u00A0"}</div>;
              })}
            </div>
          )}

          {/* Non-compliance warning card */}
          {selected.risks?.length > 0 && (
            <div className="mx-4 mb-4 rounded-2xl border-2 border-[#E5B34B] bg-[#FFF8E7] p-4" data-testid="postop-warning-card">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#E5B34B]/25 grid place-items-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-[#B0521F]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-[#B0521F]">Important — non-compliance can lead to serious complications</div>
                  <div className="text-xs text-ink-mid mt-1">Not following the above instructions may increase the risk of:</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                {selected.risks.map((r, i) => {
                  const RiskIcon = RISK_ICONS[r.icon] || AlertTriangle;
                  return (
                    <div key={i} data-testid={`postop-risk-${i}`}
                      className="bg-white border border-[#E5B34B]/60 rounded-xl px-3 py-2 flex items-start gap-2">
                      <RiskIcon className="w-4 h-4 text-[#B0521F] flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-[#B0521F] leading-tight">{r.name}</div>
                        {r.note && <div className="text-[10px] text-ink-soft leading-tight mt-0.5">{r.note}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selected.severe_warning && (
                <div className="mt-3 rounded-xl border-2 border-[#C9302C] bg-[#FFF3F3] text-[#8B1E1E] px-3 py-2.5 text-xs flex items-start gap-2" data-testid="postop-severe-warning">
                  <span className="inline-flex w-4 h-4 rounded-full bg-[#C9302C] text-white items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">!</span>
                  <span>{selected.severe_warning}</span>
                </div>
              )}
            </div>
          )}
          <div className="p-4 border-t border-[#E8EAE9] flex items-center gap-2 flex-wrap">
            <button
              data-testid="postop-send-whatsapp"
              onClick={sendWhatsApp}
              disabled={sending}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium bg-[#25D366] text-white hover:bg-[#1DAF54] transition-all disabled:opacity-60"
            >
              <MessageCircle className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send WhatsApp"}
            </button>
            <button
              data-testid="postop-print"
              onClick={printSheet}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium border border-pine/20 text-ink hover:bg-linen"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button
              data-testid="postop-email"
              onClick={emailSheet}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium border border-pine/20 text-ink hover:bg-linen"
            >
              <Mail className="w-3.5 h-3.5" /> Email
            </button>
          </div>
        </div>
      )}

      {/* History (collapsible) */}
      {historyOpen && (
        <div className="mt-4 pt-4 border-t border-[#E8EAE9]" data-testid="postop-history">
          <div className="overline text-ink-soft mb-2 text-[10px]">History</div>
          <div className="divide-y divide-[#E8EAE9]">
            {history.map((h) => (
              <div key={h.id} className="py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{h.title}</div>
                  <div className="text-[10px] text-ink-soft">
                    {new Date(h.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    {h.sent_channels?.length > 0 && ` · sent ${h.sent_channels.length}×`}
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const { data } = await api.get(`/patient-instructions/${h.id}`);
                    setSelected(data);
                    setExpanded(true);
                  }}
                  className="text-[11px] text-ink-mid hover:text-ink"
                >
                  Reopen →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
